import type {
  CertificatRamonageDocument,
  DocumentResponse,
  FileDocumentResponse,
  InvoiceDocument,
  JardinierReportDocument,
  TextDocumentResponse,
} from "@mytour/shared";

/**
 * Forme unifiée consommée par la timeline (#1229) : `source` distingue la provenance
 * (upload direct vs dérivé d'un autre agrégat), indépendamment du `kind` upload/text/file.
 */
export type DocumentFeedItem =
  | {source: "upload"; document: DocumentResponse}
  | {source: "attestation"; document: CertificatRamonageDocument}
  | {source: "jardinier"; document: JardinierReportDocument}
  | {source: "invoice"; document: InvoiceDocument};

/**
 * Note on the variant typing: we keep the discriminant + a concrete payload type per
 * variant. `Extract<DocumentResponse, {kind: "text"}>` looks tidier but collapses to `never`
 * when consumed from a workspace that compiles with `strict: false` (the `@mytour/platform`
 * package), which crashes CI even though our local strict tsconfig is fine. Using the
 * already-named response types side-steps the issue.
 */
export type FeedEntry =
  | {kind: "note"; document: TextDocumentResponse}
  | {kind: "file"; document: FileDocumentResponse}
  | {kind: "photos"; documents: FileDocumentResponse[]}
  | {kind: "attestation"; document: CertificatRamonageDocument}
  | {kind: "jardinier"; document: JardinierReportDocument}
  | {kind: "invoice"; document: InvoiceDocument};

export type FeedDayGroup = {
  dayKey: string; // YYYY-MM-DD
  date: Date;
  entries: FeedEntry[];
};

const IMAGE_MIME_PREFIX = "image/";

/**
 * Group feed items into the structure rendered by the timeline:
 *   - by day (descending — newest day first, newest entry first within each day)
 *   - within a day, consecutive image files by the same author and uploaded in the same
 *     minute collapse into a single `photos` entry (the "drop simultaneous N images = 1
 *     PhotoCard" rule from the spec). Heuristic-only — the backend does not persist a batch
 *     id, so we approximate from `memberId` + truncated `createdDate`.
 */
export function groupDocumentsForFeed(items: DocumentFeedItem[]): FeedDayGroup[] {
  const sorted = [...items].sort((a, b) => getItemDate(b).getTime() - getItemDate(a).getTime());

  const byDay = new Map<string, FeedDayGroup>();
  /**
   * Batch ouvert par clé memberId+minute, indexé à part de `entries` (pas par adjacence) :
   * un item d'une autre provenance trié entre deux photos de la même minute ne doit pas les
   * scinder en deux `photos` entries.
   */
  const openPhotoBatches = new Map<string, Extract<FeedEntry, {kind: "photos"}>>();
  for (const item of sorted) {
    const date = getItemDate(item);
    const dayKey = toDayKey(date);
    let group = byDay.get(dayKey);
    if (!group) {
      group = {dayKey, date: startOfDay(date), entries: []};
      byDay.set(dayKey, group);
    }
    appendEntry(group.entries, item, openPhotoBatches);
  }

  return Array.from(byDay.values()).sort((a, b) => b.date.getTime() - a.date.getTime());
}

/**
 * Date de tri/regroupement par jour : la date de création pour un upload, la date de
 * sauvegarde du document dérivé sinon (attestation/compte rendu, le déclencheur du #1229).
 */
function getItemDate(item: DocumentFeedItem): Date {
  if (item.source === "invoice") {
    return new Date(`${item.document.issueDate}T00:00:00`);
  }
  if (item.source === "attestation") {
    return new Date(item.document.attestationDate);
  }
  if (item.source === "jardinier") {
    return new Date(item.document.reportDate);
  }
  return new Date(item.document.createdDate);
}

function appendEntry(
  entries: FeedEntry[],
  item: DocumentFeedItem,
  openPhotoBatches: Map<string, Extract<FeedEntry, {kind: "photos"}>>
) {
  if (item.source === "attestation") {
    entries.push({kind: "attestation", document: item.document});
    return;
  }
  if (item.source === "jardinier") {
    entries.push({kind: "jardinier", document: item.document});
    return;
  }

  if (item.source === "invoice") {
    entries.push({kind: "invoice", document: item.document});
    return;
  }

  const doc = item.document;
  if (doc.kind === "text") {
    const entry: FeedEntry = {kind: "note", document: doc as TextDocumentResponse};
    entries.push(entry);
    return;
  }
  const fileDoc = doc as FileDocumentResponse;
  if (!fileDoc.mimeType.startsWith(IMAGE_MIME_PREFIX)) {
    const entry: FeedEntry = {kind: "file", document: fileDoc};
    entries.push(entry);
    return;
  }

  // Voir le commentaire sur `openPhotoBatches` dans groupDocumentsForFeed ci-dessus.
  const batchKey = `${fileDoc.memberId}:${minuteKey(fileDoc.createdDate)}`;
  const existingBatch = openPhotoBatches.get(batchKey);
  if (existingBatch) {
    existingBatch.documents.push(fileDoc);
    return;
  }

  const entry: Extract<FeedEntry, {kind: "photos"}> = {kind: "photos", documents: [fileDoc]};
  openPhotoBatches.set(batchKey, entry);
  entries.push(entry);
}

function minuteKey(date: Date | string): string {
  // Local time on purpose — day buckets are local too, so a batch must not be split or
  // re-merged across the local midnight boundary.
  const d = new Date(date);
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}-${d.getHours()}-${d.getMinutes()}`;
}

function toDayKey(date: Date): string {
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

function startOfDay(date: Date): Date {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

function pad2(n: number): string {
  return n.toString().padStart(2, "0");
}
